import { inject, Injectable, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { CacheService } from '@core/persistence'
import { User } from '@core/data'
import { AuthApi } from '../auth-api/auth-api'
import { AuthRequirementsPoller } from '../auth-requirements-poller/auth-requirements-poller'

/**
 * AuthService - Manages authentication state and user session
 *
 * Responsibility: Centralized authentication state management for the entire application.
 *
 * Rules:
 * - Stores current user as a signal (reactive)
 * - Initializes CacheService with current user (for cache validation)
 * - Provides session lifecycle methods (initialize, logout, update, login)
 * - Manages requirements polling lifecycle
 * - This is the single source of truth for "who is logged in"
 *
 * Usage:
 * - Call `initialize()` in APP_INITIALIZER (for page refresh)
 * - Call `login()` for user login
 * - Access current user with `authService.currentUser()`
 * - Components auto-update when signal changes
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  #authApi = inject(AuthApi)
  #cacheService = inject(CacheService)
  #requirementsPoller = inject(AuthRequirementsPoller)

  /**
   * Current authenticated user.
   * Signal allows components to reactively update when user changes.
   */
  currentUser = signal<User | null>(null)

  /**
   * Login with credentials.
   * Initializes session immediately (user ready before redirect).
   * @param credentials - Login form data (e.g., { email, password })
   * @returns Promise<boolean> - True if login successful
   */
  async login(credentials: Record<string, string>): Promise<boolean> {
    try {
      await firstValueFrom(this.#authApi.login(credentials))
      await this.initialize() // Load user immediately and initialize cache
      return true
    } catch {
      return false
    }
  }

  /**
   * Initialize auth state.
   * Fetches current user from backend and sets up cache validation.
   * Starts requirements polling if user is authenticated.
   * Call this in APP_INITIALIZER (for page refresh scenarios).
   * @returns Promise<void>
   */
  async initialize(): Promise<void> {
    const user = await firstValueFrom(this.#authApi.fetchCurrentUser())
    this.currentUser.set(user)

    // Only initialize cache if user is authenticated
    if (user) {
      await this.#cacheService.init(user)
      this.#requirementsPoller.start()
    }
  }

  /**
   * Set current user and initialize cache.
   * Updates signal and cache validation in one atomic operation.
   * @param user - User to set as current (null for logged out state)
   */
  async setCurrentUser(user: User | null): Promise<void> {
    this.currentUser.set(user)
    await this.#cacheService.init(user)
  }

  /**
   * Update current user properties.
   * Merges updates into current user signal.
   * @param updates - Partial user properties to update
   */
  updateCurrentUser(updates: Partial<User>): void {
    const current = this.currentUser()
    if (current) {
      this.currentUser.set({ ...current, ...updates })
    }
  }

  /**
   * Logout current user.
   * Clears user signal, cache, and stops requirements polling.
   */
  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.#authApi.logout())
    } finally {
      this.currentUser.set(null)
      this.#requirementsPoller.stop()
    }
  }

  /**
   * Check if user is authenticated.
   * @returns boolean
   */
  isAuthenticated(): boolean {
    return this.currentUser() !== null
  }
}
